import React, { useRef, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogTitle,
  useMediaQuery,
  Container,
  Fade,
  Avatar,
  Paper,
  useTheme,
  Button,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  AddCircleOutline,
  History,
  Search,
  HelpOutline,
  SupportAgent,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import TicketPage from "./TicketPage";
import { useDispatch } from "react-redux";
import * as actionTypes from "../../Redux Store/actionType/action-type";

const TicketDashboard = () => {
  const [openForm, setOpenForm] = useState(false);
  const [faqQuery, setFaqQuery] = useState("");
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  // ✅ Track which accordion is open
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false); // only one open at a time
  };

  const dashboardCards = [
    {
      title: "Create New Ticket",
      description: "Submit a new support request",
      icon: AddCircleOutline,
      color: "#667eea",
      gradient: "#1570EF",
      action: () => setOpenForm(true),
    },
    {
      title: "View Ticket History",
      description: "Check your previous tickets",
      icon: History,
      color: "#4caf50",
      gradient: "#1570EF",
      action: () => {
        dispatch({ type: actionTypes.ACTIVE_MENU, payload: "ticket-history" });
        navigate("/Home/ticket-history");
      },
    },
  ];

  // FAQ data
  const faqs = useMemo(
    () => [
      {
        q: "1. How can I register as a Channel Partner?",
        a: "Visit our CP portal, click 'Register', fill in your details, and upload the required documents. Approval usually takes 2–3 working days.",
      },
      {
        q: "2. What documents are needed for registration?",
        a: "PAN card, GST certificate, bank details (cancelled cheque), and address proof.",
      },
      {
        q: "3. How can I check my registration status?",
        a: "Log in to the CP dashboard and view the Registration Status section.",
      },
      {
        q: "4. How do I log in to the CP/FOS dashboard?",
        a: "Use your CP code and registered mobile number/email. An OTP will be sent for verification.",
      },
      {
        q: "5. What if I do not receive the OTP?",
        a: "Check your network connection and SMS inbox. If you have not received it yet, click 'Resend OTP'.",
      },
      {
        q: "6. How can I check lead status?",
        a: "Log in to the dashboard → Leads → Select the lead to view current status.",
      },
      {
        q: "7. How do I tag a booking to my CP code?",
        a: "Ensure the customer’s booking form mentions your CP code.",
      },
      {
        q: "8. How do I check for agreement details?",
        a: "The agreement details can be checked after an agreement is successfully completed in the agreement tab in your CP portal.",
      },
      {
        q: "9. How do I check invoicing details?",
        a: "The invoice information can be checked by clicking on the ‘Invoice Info’ button in the agreement tab.",
      },
      {
        q: "10. How do I raise an invoice?",
        a: "The invoice can be raised by clicking on the ‘Generate Invoice’ button in the agreement tab; a detailed guide to do the same can be found in the ‘View Guide’ button on the upper right corner of the screen in the agreement tab.",
      },
      {
        q: "11. What should be the invoice date?",
        a: "The invoice date can be any date after the agreement date.",
      },
      {
        q: "12. What is the word limit of the invoice number?",
        a: "The invoice number can be a maximum of 16 digits only in any combination of numbers, alphabets and characters.",
      },
      {
        q: "13. When will I receive my payout?",
        a: "Payments are processed after customer payments are cleared and all documents are submitted. Usual cycle: approximately 10 working days.",
      },
      {
        q: "14. How can I get the latest inventory list?",
        a: "You can also request them from your sales or CP sourcing contact.",
      },
      {
        q: "15. How can I attend training sessions?",
        a: "Training invites are sent to WhatsApp groups.",
      },
      {
        q: "16. Who should I contact for urgent queries?",
        a: "Contact your assigned Sales Manager or Sourcing Manager.",
      },
      {
        q: "17. What is the time to re-open a ticket after resolution?",
        a: "Once resolved, the ticket will auto-close within 48 hours if no response is received. You can request to re-open it anytime before auto-closure.",
      },
    ],
    []
  );

  const filteredFaqs = useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(
      (item) =>
        item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)
    );
  }, [faqQuery, faqs]);

  const submitTicketRef = useRef(null);

  return (
    <Box sx={{ minHeight: "100vh", py: 4, mt: -2 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          sx={{
            background: "linear-gradient(135deg,  #1570EF 0%,  #667eea  100%)",
            borderRadius: 4,
            p: 2,
            mb: 4,
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
              display: { xs: "none", md: "block" },
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 60,
                height: 60,
                mr: 3,
              }}
            >
              <SupportAgent sx={{ fontSize: 39 }} />
            </Avatar>
            <Box>
              <Typography
                variant={isMobile ? "h2" : "h3"}
                sx={{ fontFamily: "Rubik", fontWeight: 700, mb: 1 }}
              >
                Help Desk
              </Typography>
              <Typography
                variant="h4"
                sx={{ opacity: 0.9, fontWeight: 300, fontFamily: "Rubik" }}
              >
                Your one-stop solution for technical support
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Cards */}
        <Grid container spacing={4}>
          {dashboardCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <Fade in timeout={600 + index * 200}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                    transition:
                      "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                    "&:hover": {
                      transform: "translateY(-12px) scale(1.02)",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                    },
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      background: card.gradient,
                    }}
                  />
                  <CardActionArea
                    onClick={card.action}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      p: 0,
                    }}
                  >
                    <CardContent
                      sx={{
                        textAlign: "center",
                        p: 4,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <Box sx={{ mb: 3, position: "relative" }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            m: "0 auto",
                            background: card.gradient,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                          }}
                        >
                          <card.icon sx={{ fontSize: 40 }} />
                        </Avatar>
                      </Box>
                      <Typography
                        variant={isMobile ? "h3" : "h4"}
                        sx={{
                          fontFamily: "Rubik",
                          fontWeight: 600,
                          mb: 2,
                          color: "text.primary",
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        variant="body3"
                        sx={{ color: "text.secondary", lineHeight: 1.6 }}
                      >
                        {card.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* FAQ Section */}
        <Paper
          sx={{
            mt: 5,
            p: { xs: 2.5, sm: 4 },
            borderRadius: 4,
            boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
            backgroundColor: "white",
          }}
        >
          {/* Heading */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              gap: 1.5,
            }}
          >
            <HelpOutline
              sx={{ fontSize: isMobile ? 28 : 36, color: "#1570EF" }}
            />
            <Typography
              sx={{
                fontFamily: "Rubik",
                fontWeight: 700,
                fontSize: isMobile ? 16 : 30,
              }}
            >
              Frequently Asked Questions
            </Typography>
          </Box>

          {/* Search */}
          <Box sx={{ maxWidth: 900, mx: "auto", mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search FAQs..."
              value={faqQuery}
              onChange={(e) => setFaqQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: isMobile ? "1.6rem" : "1.2rem",
                  fontFamily: "Rubik",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: 3 },
                "& input::placeholder": {
                  fontSize: isMobile ? "1.6rem" : "1.2rem",
                  fontFamily: "Rubik",
                },
              }}
            />
          </Box>

          {/* FAQ Cards */}
          <Box sx={{ maxWidth: 900, mx: "auto" }}>
            {filteredFaqs.length === 0 ? (
              <Typography
                sx={{
                  textAlign: "center",
                  color: "text.secondary",
                  py: 4,
                  fontSize: isMobile ? "1.8rem" : "1.5rem",
                }}
              >
                No FAQs match your search.
              </Typography>
            ) : (
              filteredFaqs.map((item, i) => (
                <Fade in timeout={500 + i * 150} key={i}>
                  <Box
                    sx={{
                      mb: 2.5,
                      borderRadius: 3,
                      border: "1px solid #d0d7de",
                      backgroundColor: "#fff",
                      boxShadow: "0 6px 12px rgba(0,0,0,0.06)",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 10px 20px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    <Accordion
                      expanded={expanded === `panel${i}`}    // ✅ unique id
                      onChange={handleChange(`panel${i}`)}  // ✅ toggle
                      disableGutters
                      elevation={0}
                      sx={{
                        "&:before": { display: "none" },
                        boxShadow: "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon sx={{ color: "#1570EF", fontSize: isMobile ? "3rem" : "2rem",  }} />
                        }
                        sx={{
                          px: 3,
                          py: 2,
                          "& .MuiAccordionSummary-content": { my: 0 },
                        }}
                      >
                        <Typography
                          variant={isMobile ? "h4" : "h5"}
                          sx={{
                            fontFamily: "Rubik",
                            fontWeight: 700,
                            fontSize: isMobile ? "2rem" : "1.7rem",
                          }}
                        >
                          {item.q}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          px: 3,
                          pt: 1.5,
                          pb: 2.5,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.7,
                            fontSize: isMobile ? "1.8rem" : "1.5rem",
                          }}
                        >
                          {item.a}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Fade>
              ))
            )}
          </Box>
        </Paper>
      </Container>

      {/* Create Ticket Modal */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            height: "90vh",
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Rubik",
            fontWeight: 700,
            fontSize: isMobile ? "2.5rem" : "2rem",
            background: "linear-gradient(135deg,  #1570EF 0%,  #667eea  100%)",
            color: "#fff",
            py: 3,
          }}
        >
          Create Support Ticket
        </DialogTitle>

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <TicketPage
            onClose={() => setOpenForm(false)}
            setSubmitRef={(fn) => (submitTicketRef.current = fn)}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default TicketDashboard;
